import { lazy, Suspense, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Eye, Loader2 } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'sonner'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { resolveIcon } from '@/lib/documentTypeIcon'
import { useUser } from '@/contexts/UserContext'
import { Features } from '@/lib/features'
import { isPdf } from '@/lib/isPdf'
import {
  getDocument,
  listDocumentVersions,
  downloadDocument,
  markDocumentViewed,
  markDocumentDownloaded,
  documentKeys,
} from '@/lib/api/documents'

// Lazy de propósito: pdfjs-dist pesa ~1MB e não pode entrar no bundle inicial
// de quem nunca abre um PDF.
const PdfViewerModal = lazy(() => import('@/components/ged/PdfViewerModal'))

type Props = {
  documentId: string | null
  onClose: () => void
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function FileDrawer({ documentId, onClose }: Props) {
  const qc = useQueryClient()
  const [isDownloading, setIsDownloading] = useState(false)
  const { isFeatureEnabled } = useUser()
  const [isViewerOpen, setIsViewerOpen] = useState(false)
  // O chunk do PdfViewerModal tem 641 KB — em conexão lenta ou no primeiro
  // uso da sessão, o import() do lazy() demora o suficiente pra parecer que
  // o clique não fez nada. Esse estado cobre só o intervalo entre o clique
  // e o modal montar (o próprio modal assume o feedback visual dali em diante).
  const [isViewerLoading, setIsViewerLoading] = useState(false)

  // Espera o chunk lazy chegar ANTES de montar o modal, em vez de tentar
  // detectar a montagem de dentro do Suspense. O import() é memoizado pelo
  // bundler, então o lazy() logo abaixo resolve na hora — e o botão fica em
  // estado de carregamento exatamente durante o download do chunk.
  async function openViewer() {
    setIsViewerLoading(true)
    try {
      await import('@/components/ged/PdfViewerModal')
    } finally {
      setIsViewerLoading(false)
      setIsViewerOpen(true)
    }
  }

  const { data: doc, isLoading } = useQuery({
    queryKey: documentKeys.detail(documentId ?? ''),
    queryFn: () => getDocument(documentId!),
    enabled: !!documentId,
  })

  const { data: versions = [] } = useQuery({
    queryKey: documentKeys.versions(documentId ?? ''),
    queryFn: () => listDocumentVersions(documentId!),
    enabled: !!documentId,
  })

  useEffect(() => {
    if (!documentId) return
    void markDocumentViewed(documentId)
      .then(() => qc.invalidateQueries({ queryKey: ['documents'] }))
      .catch(() => { /* swallow */ })
  }, [documentId, qc])

  async function handleDownload() {
    if (!documentId || !doc) return
    setIsDownloading(true)
    try {
      const blob = await downloadDocument(documentId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.originalFilename
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Erro ao baixar documento')
      return
    } finally {
      setIsDownloading(false)
    }
    // Tracking pessoal — falha do markDocumentDownloaded (ex.: 404 quando o user
    // não tem ProfileAreaPermission explícita nessa área, mesmo tendo aberto via
    // admin bypass) não deve sinalizar erro de download pro usuário. O download
    // real já terminou com sucesso acima.
    markDocumentDownloaded(documentId)
      .then(() => {
        qc.invalidateQueries({ queryKey: ['documents'] })
        qc.invalidateQueries({ queryKey: ['me'] })
      })
      .catch(() => { /* analytics-only; downloaded already succeeded */ })
  }

  const Icon = doc ? resolveIcon(doc.typeIconName) : null
  const canPreview = !!doc && isFeatureEnabled(Features.PdfViewer) && isPdf(doc)

  return (
    <>
    <Sheet open={!!documentId} onOpenChange={(open) => { if (!open) onClose() }}>
      <SheetContent
        side="right"
        style={{
          width: 420,
          padding: 0,
          background: 'var(--ev-bg-elevated)',
          borderLeft: '1px solid var(--ev-border)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <SheetHeader
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--ev-divider)',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <SheetTitle
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--ev-fg-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: 0,
            }}
          >
            Detalhes do documento
          </SheetTitle>
        </SheetHeader>

        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {isLoading && (
            <p style={{ fontSize: 13, color: 'var(--ev-fg-muted)' }}>Carregando…</p>
          )}
          {doc && Icon && (
            <>
              {/* Title + icon */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
                <div
                  style={{
                    width: 36,
                    height: 40,
                    background: (doc.typeColorHex || '#98A1AE') + '18',
                    color: doc.typeColorHex || '#98A1AE',
                    borderRadius: 4,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={20} />
                </div>
                <h3 style={{ fontSize: 16, color: 'var(--ev-fg-strong)', lineHeight: 1.3, margin: 0 }}>
                  {doc.name}
                </h3>
              </div>

              {/* Badges */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 22,
                    padding: '0 8px',
                    fontSize: 11,
                    fontWeight: 500,
                    borderRadius: 4,
                    background: 'var(--ev-bg-alt)',
                    color: 'var(--ev-fg-muted)',
                  }}
                >
                  {doc.typeName}
                </span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 22,
                    padding: '0 8px',
                    fontSize: 11,
                    fontWeight: 500,
                    borderRadius: 4,
                    background: 'var(--ev-bg-alt)',
                    color: 'var(--ev-fg-muted)',
                  }}
                >
                  {formatBytes(doc.fileSizeBytes)}
                </span>
              </div>

              {/* Ações: visualizar (verde) + baixar (azul) */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
                {canPreview && (
                  <Button
                    onClick={openViewer}
                    disabled={isViewerLoading}
                    style={{
                      flex: 1,
                      justifyContent: 'center',
                      background: 'var(--ev-positive)',
                      color: 'white',
                      border: 0,
                      opacity: isViewerLoading ? 0.7 : 1,
                    }}
                  >
                    {isViewerLoading
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Eye size={14} />}
                    Visualizar
                  </Button>
                )}
                <Button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  style={{
                    flex: 1,
                    justifyContent: 'center',
                    background: 'var(--ev-accent)',
                    color: 'white',
                    border: 0,
                    opacity: isDownloading ? 0.7 : 1,
                  }}
                >
                  {isDownloading
                    ? <Loader2 size={14} className="animate-spin" />
                    : <Download size={14} />}
                  {isDownloading ? 'Baixando…' : 'Baixar documento'}
                </Button>
              </div>

              {/* Metadata */}
              <div style={{ marginBottom: 24 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--ev-fg-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: 10,
                  }}
                >
                  Metadados
                </div>
                {(
                  [
                    ['Tipo', doc.typeName],
                    ['Área', doc.areaName],
                    ['Data do documento', format(parseISO(doc.documentDate), 'dd/MM/yyyy', { locale: ptBR })],
                    ['Arquivo original', doc.originalFilename],
                    ['Tamanho', formatBytes(doc.fileSizeBytes)],
                    ['Versões', String(doc.versionCount)],
                  ] as [string, string][]
                ).map(([key, value]) => (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '6px 0',
                      fontSize: 12,
                      borderBottom: '1px solid var(--ev-divider)',
                    }}
                  >
                    <span style={{ color: 'var(--ev-fg-muted)' }}>{key}</span>
                    <span
                      style={{
                        color: 'var(--ev-fg)',
                        fontWeight: 500,
                        maxWidth: 220,
                        textAlign: 'right',
                        wordBreak: 'break-word',
                      }}
                    >
                      {value}
                    </span>
                  </div>
                ))}
                {doc.description && (
                  <div style={{ marginTop: 10, fontSize: 12, color: 'var(--ev-fg-muted)', lineHeight: 1.5 }}>
                    {doc.description}
                  </div>
                )}
              </div>

              {/* Version history */}
              {versions.length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--ev-fg-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: 10,
                    }}
                  >
                    Histórico ({versions.length} {versions.length === 1 ? 'versão' : 'versões'})
                  </div>
                  {versions.map((v) => (
                    <div
                      key={v.id}
                      style={{
                        display: 'flex',
                        gap: 10,
                        padding: '10px 0',
                        fontSize: 12,
                        borderBottom: '1px solid var(--ev-divider)',
                      }}
                    >
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: 'var(--ev-accent)',
                          marginTop: 5,
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>
                          v{v.versionNumber} · {v.originalFilename}
                        </div>
                        <div style={{ color: 'var(--ev-fg-muted)', fontSize: 11 }}>
                          {format(parseISO(v.uploadedAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                          {v.notes && ` · ${v.notes}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>

    {/* Rede de segurança: openViewer já esperou o chunk, então o lazy() resolve
        na hora e o fallback do Suspense não chega a aparecer. */}
    {isViewerOpen && doc && (
      <Suspense fallback={null}>
        <PdfViewerModal
          key={doc.id}
          documentId={doc.id}
          filename={doc.originalFilename}
          onClose={() => setIsViewerOpen(false)}
        />
      </Suspense>
    )}
    </>
  )
}
